import { useCallback, useEffect, useMemo, useState } from "react";
import {
  applyGeocodeQueueEntry,
  getGeocodeQueue,
  getGeocodeQueueSummary,
  retryGeocodeQueueEntry,
  skipGeocodeQueueEntry,
} from "../../../services/midpointApi";
import AdminStatePanel from "./layout/AdminStatePanel";

const GeocodeQueuePage = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [manualCoords, setManualCoords] = useState({});

  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [queueData, summaryData] = await Promise.all([
        getGeocodeQueue({ status: statusFilter || undefined, limit: 100 }),
        getGeocodeQueueSummary(),
      ]);
      setItems(queueData.items || []);
      setSummary(summaryData);
    } catch (loadError) {
      setItems([]);
      setSummary(null);
      setError(loadError.message || "Unable to load geocoding queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const runAction = async (action, queueId) => {
    try {
      setActionError("");
      if (action === "skip") {
        await skipGeocodeQueueEntry(queueId);
      } else if (action === "retry") {
        await retryGeocodeQueueEntry(queueId);
      } else if (action === "apply") {
        const coords = manualCoords[queueId];
        if (!coords?.latitude || !coords?.longitude) {
          setActionError("Enter latitude and longitude before applying.");
          return;
        }
        await applyGeocodeQueueEntry(queueId, {
          latitude: Number(coords.latitude),
          longitude: Number(coords.longitude),
        });
      }
      await loadQueue();
    } catch (actionErr) {
      setActionError(actionErr.message || "Action failed.");
    }
  };

  const queueStats = summary?.queue || {};
  const listingStats = summary?.listings || {};

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Queued", value: queueStats.queued ?? 0 },
      { label: "Needs Review", value: queueStats.needs_review ?? 0, tone: (queueStats.needs_review ?? 0) > 0 ? "amber" : null },
      { label: "Completed", value: queueStats.completed ?? 0, tone: "green" },
      { label: "Failed", value: queueStats.failed ?? 0, tone: (queueStats.failed ?? 0) > 0 ? "red" : null },
      { label: "With Coords", value: listingStats.with_coordinates ?? 0, tone: "green" },
      { label: "Missing Coords", value: listingStats.missing_coordinates ?? 0, tone: (listingStats.missing_coordinates ?? 0) > 0 ? "amber" : null },
    ];
  }, [summary, queueStats, listingStats]);

  return (
    <div className="admin-dashboard admin-geocode-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Geocoding Queue</h1>
          <p className="admin-dashboard__subtitle">
            Review listings queued for geocoding — retry, skip, or manually apply coordinates
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadQueue}
            disabled={loading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!loading && !error && summary && (
        <section className="admin-dashboard__kpis">
          {kpis.map((kpi) => (
            <div className="admin-dash__kpi-card" key={kpi.label}>
              <span className="admin-dash__kpi-label">{kpi.label}</span>
              <span
                className={`admin-dash__kpi-value${
                  kpi.tone === "green"
                    ? " admin-dash__kpi-value--green"
                    : kpi.tone === "red"
                      ? " admin-dash__kpi-value--red"
                      : kpi.tone === "amber"
                        ? " admin-dash__kpi-value--amber"
                        : ""
                }`}
              >
                {kpi.value}
              </span>
            </div>
          ))}
          {listingStats.average_quality_score != null && (
            <div className="admin-dash__kpi-card">
              <span className="admin-dash__kpi-label">Avg Quality</span>
              <span className="admin-dash__kpi-value">
                {listingStats.average_quality_score}
              </span>
            </div>
          )}
        </section>
      )}

      {actionError && (
        <div className="admin-re-toast admin-re-toast--error" role="alert">
          {actionError}
        </div>
      )}

      <section className="admin-dash__card admin-dash__card--wide admin-geocode-dash__queue-card">
        <div className="admin-geocode-dash__toolbar">
          <div className="admin-geocode-dash__filters">
            <select
              className="admin-source-controls-dash__select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="QUEUED">Queued</option>
              <option value="PROCESSING">Processing</option>
              <option value="NEEDS_REVIEW">Needs review</option>
              <option value="FAILED">Failed</option>
              <option value="COMPLETED">Completed</option>
              <option value="SKIPPED">Skipped</option>
            </select>
            <span className="admin-geocode-dash__item-count">
              {items.length} item{items.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {loading ? <AdminStatePanel variant="loading" message="Loading geocoding queue…" /> : null}

        {!loading && error ? (
          <AdminStatePanel variant="error" message={error} onRetry={loadQueue} />
        ) : null}

        {!loading && !error && items.length === 0 ? (
          <div className="admin-geocode-dash__empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <p className="muted">No queue items match this filter.</p>
          </div>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div className="admin-geocode-dash__list">
            {items.map((item) => (
              <article key={item.id} className="admin-geocode-dash__item">
                <div className="admin-geocode-dash__item-header">
                  <div className="admin-geocode-dash__item-title">
                    <strong>{item.propertyTitle || item.externalId || item.propertyId}</strong>
                    <span
                      className={`admin-canonical-dash__status-pill${
                        item.status === "COMPLETED"
                          ? " admin-canonical-dash__status-pill--active"
                          : item.status === "FAILED"
                            ? " admin-geocode-dash__status--failed"
                            : item.status === "NEEDS_REVIEW"
                              ? " admin-canonical-dash__status-pill--hidden"
                              : ""
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                  <div className="admin-geocode-dash__item-meta">
                    <span>{item.source}</span>
                    <span>priority {item.priority}</span>
                    <span>
                      {item.attemptCount}/{item.maxAttempts} attempts
                    </span>
                    {item.confidence != null && <span>confidence {item.confidence}</span>}
                  </div>
                </div>

                <div className="admin-geocode-dash__item-address">
                  <p>{item.candidateAddress}</p>
                  <p className="muted">
                    {[item.neighborhood, item.city, item.department].filter(Boolean).join(", ")}
                  </p>
                </div>

                {item.errorMessage && (
                  <p className="admin-csv-import-dash__error">{item.errorMessage}</p>
                )}

                <div className="admin-geocode-dash__item-actions">
                  <button
                    type="button"
                    className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
                    onClick={() => runAction("retry", item.id)}
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__action-btn admin-canonical-dash__action-danger"
                    onClick={() => runAction("skip", item.id)}
                  >
                    Skip
                  </button>
                  <div className="admin-geocode-dash__coord-inputs">
                    <input
                      type="number"
                      step="any"
                      placeholder="Lat"
                      className="admin-geocode-dash__coord-input"
                      value={manualCoords[item.id]?.latitude || ""}
                      onChange={(event) =>
                        setManualCoords((current) => ({
                          ...current,
                          [item.id]: {
                            ...current[item.id],
                            latitude: event.target.value,
                          },
                        }))
                      }
                    />
                    <input
                      type="number"
                      step="any"
                      placeholder="Lng"
                      className="admin-geocode-dash__coord-input"
                      value={manualCoords[item.id]?.longitude || ""}
                      onChange={(event) =>
                        setManualCoords((current) => ({
                          ...current,
                          [item.id]: {
                            ...current[item.id],
                            longitude: event.target.value,
                          },
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="admin-dashboard__action-btn"
                      onClick={() => runAction("apply", item.id)}
                    >
                      Apply
                    </button>
                  </div>
                  {item.detailUrl && (
                    <a
                      href={item.detailUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-geocode-dash__listing-link"
                    >
                      View listing →
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default GeocodeQueuePage;
